import BoltOnRenewalSummary from '../BoltOnRenewalSummary';
// components/teams/detailCards/modals/BoltOnManageModal.jsx
import React from 'react';
import ReactDOM from 'react-dom';
import SubmitButton from '../../../forms/formComponents/SubmitButton';
import ErrorMessage from '../../../forms/formComponents/ErrorMessage';

import { functions, auth } from '../../../../firebase-client';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '../../../../auth/AuthContext';

function formatWhen(d) {
  if (!d) return null;
  // Supports Firestore Timestamp, number (ms), or ISO string
  try {
    let date;
    if (typeof d?.toDate === 'function') date = d.toDate();
    else if (typeof d === 'number') date = new Date(d);
    else date = new Date(String(d));
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return null;
  }
}

function formatStatus(entry) {
  if (!entry) return 'None';

  if (entry.mode === 'subscription') {
    if (entry.status === 'active') return 'Active (subscription)';
    if (entry.status === 'cancel_at_period_end') return 'Cancels at period end';
    return entry.status || 'Unknown subscription status';
  }

  if (entry.mode === 'manual') {
    if (entry.manualRenewalsStopped || entry.autoRenew === false) {
      return 'Cancels at period end';
    }
    return 'Active (manual)';
  }

  return 'Unknown';
}


export default function BoltOnManageModal({ teamId, boltOn, myEntry, currency = 'GBP', onClose }) {
  const { user, authLoading } = useAuth();
  const [error, setError] = React.useState(null);
  const [submitting, setSubmitting] = React.useState(false);

  const ensureSignedIn = async () => {
    const u = auth.currentUser;
    if (!u) throw new Error('You need to be signed in to manage bolt‑ons.');
    await u.getIdToken(true);
    return u;
  };

  const stopManualRenewals = async () => {
    setSubmitting(true); setError(null);
    try {
      await ensureSignedIn();
      const stopFn = httpsCallable(functions, 'stopManualBoltOn');
      await stopFn({ teamId, boltOnId: boltOn.id });
      onClose();
    } catch (e) {
      setError(e?.message || 'Failed to stop manual renewals.');
      setSubmitting(false);
    }
  };

  const cancelSubscription = async () => {
    setSubmitting(true); setError(null);
    try {
      await ensureSignedIn();
      const cancelFn = httpsCallable(functions, 'cancelBoltOn');
      await cancelFn({ teamId, boltOnId: boltOn.id });
      onClose();
    } catch (e) {
      setError(e?.message || 'Failed to cancel subscription.');
      setSubmitting(false);
    }
  };

  // ── Derived state for “pending cancellation” and next/period end date ─────────
  const isPendingCancellation =
    myEntry?.status === 'cancel_at_period_end' ||
    myEntry?.cancelAtPeriodEnd === true ||
    myEntry?.manualRenewalsStopped === true ||
    myEntry?.autoRenew === false; // optional guard for manual

  const cancellationDate =
    formatWhen(myEntry?.paidThrough) ||
    formatWhen(myEntry?.currentPeriodEnd) ||
    formatWhen(myEntry?.nextChargeDate) ||
    formatWhen(myEntry?.renewalDate) ||
    null;

  const disabled = submitting || authLoading || !user;

  // Button label + disabled state per mode
  const showSubButton = myEntry?.mode === 'subscription';
  const showManualButton = myEntry?.mode === 'manual';

  const subButtonLabel = isPendingCancellation
    ? 'Cancelling at period end'
    : (submitting ? 'Cancelling…' : 'Cancel subscription');

  const manualButtonLabel = isPendingCancellation
    ? 'Manual renewals stopped'
    : (submitting ? 'Stopping…' : 'Stop manual renewals');

  const subButtonDisabled = disabled || isPendingCancellation;
  const manualButtonDisabled = disabled || isPendingCancellation;

  return ReactDOM.createPortal(
    <div className="modal-backdrop">
      <div className="modal-content">
        <h2 className="modal-title">Manage {boltOn.name}</h2>

        {error && <ErrorMessage message={error} />}

        {!authLoading && !user && (
          <ErrorMessage message="You’re signed out. Please sign in to manage this bolt‑on." />
        )}

        <p>
        Status: <strong>{formatStatus(myEntry)}</strong>
      </p>

        <p>
          Quantity: <strong>{Math.max(1, Number(myEntry?.quantity || 1))}</strong>
        </p>


        <BoltOnRenewalSummary entry={myEntry} currency={currency} />

        {isPendingCancellation && (
          <div className="muted-note" style={{ marginTop: 8, color: 'var(--text-muted,#666)' }}>
            {myEntry?.mode === 'subscription'
              ? <>This subscription will end at the current period end{cancellationDate ? <> ({cancellationDate})</> : null}.</>
              : <>Manual renewals are stopped; access continues until the end of the current period{cancellationDate ? <> ({cancellationDate})</> : null}.</>}
          </div>
        )}

        <div className="form-actions" style={{ marginTop: 16 }}>
          <SubmitButton
            type="button"
            variant="secondary"
            label="Close"
            onClick={onClose}
            disabled={submitting}
          />

          {showSubButton && (
            <SubmitButton
              type="button"
              variant="secondary"
              label={subButtonLabel}
              onClick={cancelSubscription}
              disabled={subButtonDisabled}
            />
          )}

          {showManualButton && (
            <SubmitButton
              type="button"
              variant="secondary"
              label={manualButtonLabel}
              onClick={stopManualRenewals}
              disabled={manualButtonDisabled}
            />
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
